/**
 * PaymentGatewaysSettings — "إعدادات بوابات الدفع" screen.
 *
 * Per-provider (Stripe / Tamara / Tabby / Ziina) matrix that lets the admin
 * enable/disable each provider on each Finance sub-platform (CloudGM Customers,
 * CloudGM Merchants, 606 Fitness, CGM Coin, 606 Community, 606 MediaDocs).
 *
 * Reads/writes: /api/admin/payment-methods
 *   POST /{id}/toggle-platform  { platform, enabled }
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ToggleLeft, ToggleRight, Loader2, RefreshCw, Info, Percent, Check, X,
} from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { getAuthHeaders } from '../../utils/authHeaders';
import { FINANCE_PLATFORM_META } from '../../constants/financePlatforms';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentGatewaysSettings() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  // iter-286 (Feb 2026) — inline fee editor: which method is being edited,
  // and the current draft { fee_percent, fee_enabled } for that method.
  const [editingFee, setEditingFee] = useState(null); // method_id or null
  const [feeDraft, setFeeDraft] = useState({ fee_percent: 0, fee_enabled: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/payment-methods`, { headers: getAuthHeaders() });
      setMethods(r.data.data || []);
    } catch {
      toast.error(t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (m, platformId, next) => {
    setBusy(`${m.id}:${platformId}`);
    try {
      await axios.post(
        `${API_URL}/api/admin/payment-methods/${m.id}/toggle-platform`,
        { platform: platformId, enabled: next },
        { headers: getAuthHeaders() },
      );
      toast.success(next ? t('تم التفعيل', 'Enabled') : t('تم الإيقاف', 'Disabled'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التحديث', 'Update failed'));
    } finally {
      setBusy(null);
    }
  };

  // iter-286 — Fee editor helpers
  const openFeeEditor = (m) => {
    setEditingFee(m.id);
    setFeeDraft({
      fee_percent: Number(m.fee_percent || 0),
      fee_enabled: Boolean(m.fee_enabled),
    });
  };

  const cancelFeeEditor = () => {
    setEditingFee(null);
  };

  const saveFee = async (m) => {
    setBusy(`fee:${m.id}`);
    try {
      await axios.post(
        `${API_URL}/api/admin/payment-methods/${m.id}/set-fee`,
        {
          fee_percent: Number(feeDraft.fee_percent) || 0,
          fee_type: "percentage",
          fee_fixed_amount: 0,
          fee_enabled: feeDraft.fee_enabled,
        },
        { headers: getAuthHeaders() },
      );
      toast.success(t('تم حفظ النسبة', 'Fee saved'));
      setEditingFee(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance/settings" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
            { label_ar: 'إعدادات القسم المالي', label_en: 'Finance Settings', path: '/dashboard/finance/settings' },
            { label_ar: 'إعدادات بوابات الدفع', label_en: 'Payment Gateways', path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div className={isAr ? 'text-right' : 'text-left'}>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {t('إعدادات بوابات الدفع', 'Payment Gateways')}
            </h1>
            <p className="text-white/50 text-sm max-w-2xl">
              {t('كل مزود روحه — يمكنك تشغيل تابي على منصة، وإيقافه على أخرى، ونفس الشيء لتمارا وستريب وزينة.',
                 'Each provider is independent — turn Tabby on for one platform, off for another, and the same for Tamara / Stripe / Ziina.')}
            </p>
          </div>
          <Button
            onClick={load}
            variant="outline"
            className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
            data-testid="gateways-refresh"
          >
            <RefreshCw className={`w-4 h-4 me-1 ${loading ? 'animate-spin' : ''}`} />
            {t('تحديث', 'Refresh')}
          </Button>
        </div>

        {loading ? (
          <div className="p-16 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/20 text-blue-200/80 text-xs flex items-start gap-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                {t('التعطيل هنا يعني أن هذه البوابة لن تظهر في نموذج إنشاء الفاتورة على تلك المنصة تحديداً.',
                   'Disabling a gateway here hides it from the invoice creation form on that specific platform.')}
              </p>
            </div>

            {methods.map((m) => (
              <div key={m.id}
                   className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden"
                   data-testid={`gateways-provider-${m.provider}`}>
                <div className="flex items-center gap-4 p-5 border-b border-white/5">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden bg-white/[0.02]"
                       style={{ border: `1px solid ${m.color}30` }}>
                    {m.logo_url ? (
                      <img src={m.logo_url} alt="" className="w-full h-full object-contain p-1" />
                    ) : (
                      <span className="text-lg font-bold" style={{ color: m.color }}>{m.display_name?.[0]}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-white font-bold text-lg">{isAr ? m.display_name_ar : m.display_name}</h3>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        m.enabled ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/5 text-white/40'
                      }`}>
                        {m.enabled ? t('مفعّل عام', 'globally on') : t('معطّل عام', 'globally off')}
                      </span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        m.ready ? 'bg-blue-500/15 text-blue-300' : 'bg-amber-500/15 text-amber-300'
                      }`}>
                        {m.ready ? m.env : t('يحتاج مفتاح', 'setup')}
                      </span>
                    </div>
                    <p className="text-white/50 text-xs mt-1">
                      {isAr ? m.description_ar : m.description_en}
                    </p>
                  </div>
                </div>

                {/* iter-286 — Inline fee editor row (per-provider %) */}
                <div className="mx-5 mb-4 p-3 rounded-xl border border-white/10 bg-white/[0.02]"
                     data-testid={`gateway-fee-row-${m.provider}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-xs text-white/70">
                      <Percent className="w-3.5 h-3.5 text-amber-300" />
                      <span>{t('نسبة الرسوم على العميل', 'Customer fee %')}</span>
                    </div>

                    {editingFee === m.id ? (
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1.5 text-[11px] text-white/60">
                          <input
                            type="checkbox"
                            checked={feeDraft.fee_enabled}
                            onChange={(e) => setFeeDraft({ ...feeDraft, fee_enabled: e.target.checked })}
                            className="w-3.5 h-3.5 accent-emerald-500"
                            data-testid={`gateway-fee-enabled-${m.provider}`}
                          />
                          {t('مطبّق', 'active')}
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={feeDraft.fee_percent}
                          onChange={(e) => setFeeDraft({ ...feeDraft, fee_percent: e.target.value })}
                          className="w-24 h-8 px-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm text-center focus:outline-none focus:border-amber-500/40"
                          data-testid={`gateway-fee-input-${m.provider}`}
                        />
                        <span className="text-white/40 text-sm">%</span>
                        <button
                          onClick={() => saveFee(m)}
                          disabled={busy === `fee:${m.id}`}
                          className="p-1.5 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 disabled:opacity-50"
                          data-testid={`gateway-fee-save-${m.provider}`}
                          title={t('حفظ', 'Save')}
                        >
                          {busy === `fee:${m.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={cancelFeeEditor}
                          className="p-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/60"
                          data-testid={`gateway-fee-cancel-${m.provider}`}
                          title={t('إلغاء', 'Cancel')}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold tabular-nums ${
                          m.fee_enabled ? 'text-amber-300' : 'text-white/40'
                        }`} data-testid={`gateway-fee-value-${m.provider}`}>
                          {Number(m.fee_percent || 0).toFixed(2)}%
                          {!m.fee_enabled && (
                            <span className="text-[10px] text-white/40 ms-1">({t('غير مطبّق', 'inactive')})</span>
                          )}
                        </span>
                        <button
                          onClick={() => openFeeEditor(m)}
                          className="text-xs px-2.5 h-7 rounded-md bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 font-medium"
                          data-testid={`gateway-fee-edit-${m.provider}`}
                        >
                          {t('تعديل', 'Edit')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-5">
                  {Object.entries(FINANCE_PLATFORM_META).map(([platformId, meta]) => {
                    const isEnabled = (m.platforms || []).includes(platformId);
                    const isBusy = busy === `${m.id}:${platformId}`;
                    return (
                      <div key={platformId}
                           className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                             isEnabled
                               ? 'bg-emerald-500/5 border-emerald-500/20'
                               : 'bg-white/[0.02] border-white/10'
                           }`}
                           data-testid={`gateway-toggle-${m.provider}-${platformId}`}>
                        <div className="w-2 h-2 rounded-full flex-shrink-0"
                             style={{ backgroundColor: meta.color }} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white font-medium truncate">{isAr ? meta.ar : meta.en}</div>
                          <div className={`text-[11px] ${isEnabled ? 'text-emerald-400' : 'text-white/40'}`}>
                            {isEnabled ? t('مُفعّل', 'Enabled') : t('مُعطّل', 'Disabled')}
                          </div>
                        </div>
                        <button
                          onClick={() => toggle(m, platformId, !isEnabled)}
                          disabled={isBusy}
                          className="p-1.5 rounded-md hover:bg-white/5 disabled:opacity-50"
                          data-testid={`gateway-toggle-btn-${m.provider}-${platformId}`}
                        >
                          {isBusy ? (
                            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
                          ) : isEnabled ? (
                            <ToggleRight className="w-8 h-8 text-emerald-400" />
                          ) : (
                            <ToggleLeft className="w-8 h-8 text-white/30" />
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
